<template>
  <div>
    <v-card-title>
      <span class="subtitle-1 grey--text">{{ subtitulo }}</span>
      <v-spacer/>
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Buscar"
        single-line
        hide-details
        style="max-width: 250px"
      />
      <v-btn @click="openModal()" class="mx-2 py-0" color="primary">
        <v-icon>add_circle</v-icon>
      </v-btn>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="items"
      :search="search"
      :items-per-page="10"
      class="elevation-1"
      :no-data-text="`No hay ${plural} cargados`"
      no-results-text="No se encontraron resultados"
    >
      <template v-slot:item.active="{ item }">
        <v-chip :color="item.active !== false ? 'success' : 'error'" dark small>
          {{ item.active !== false ? 'Activo' : 'Inactivo' }}
        </v-chip>
      </template>

      <template v-slot:item.usos="{ item }">
        <v-chip v-if="getUsos(item) !== null" color="info" dark small>{{ getUsos(item) }}</v-chip>
        <span v-else class="grey--text text--lighten-1">-</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" @click="editItem(item)">edit</v-icon>
        <v-icon small @click="deleteItem(item)">delete</v-icon>
      </template>
    </v-data-table>

    <!-- Modal crear / editar -->
    <v-dialog v-model="dialog" width="520" persistent>
      <ValidationObserver ref="obs" v-slot="{ handleSubmit }">
        <v-card>
          <v-card-title class="headline">
            {{ edit ? 'Editar' : 'Crear' }} {{ singular }}
          </v-card-title>

          <v-form
            @submit.prevent="handleSubmit(formSubmit)"
            ref="form"
            lazy-validation
            class="container"
          >
            <v-row no-gutters>
              <v-col cols="12">
                <ValidationProvider rules="required|max:80" name="nombre" v-slot="{ errors, valid }">
                  <v-text-field
                    v-model="form.name"
                    name="nombre"
                    :label="`Nombre del ${singular}:`"
                    :placeholder="placeholder"
                    :error-messages="errors"
                    :success="valid"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col cols="12">
                <v-switch
                  v-model="form.active"
                  :label="form.active ? 'Visible en la app movil' : 'Oculto en la app movil'"
                  color="success"
                />
              </v-col>
            </v-row>

            <v-alert v-if="!form.active" type="info" dense text>
              Los PQR ya registrados con este nombre no se ven afectados.
            </v-alert>

            <v-card-actions>
              <v-spacer/>
              <v-btn :disabled="guardando" @click="dialog = false">Cancelar</v-btn>
              <v-btn type="submit" color="primary" :disabled="guardando" :loading="guardando">
                {{ edit ? 'Actualizar' : 'Guardar' }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <!-- Confirmacion de borrado -->
    <v-dialog v-model="deleteDialog" width="480">
      <v-card v-if="toDelete">
        <v-card-title class="headline">Eliminar {{ singular }}</v-card-title>
        <v-card-text>
          <p>Vas a eliminar <strong>{{ toDelete.name }}</strong>.</p>
          <v-alert v-if="getUsos(toDelete) === null" type="warning" dense text>
            No se puede verificar cuantos PQR quedarian asociados con tu perfil.
            Consultá con un administrador antes de eliminar.
          </v-alert>
          <v-alert v-if="getUsos(toDelete) > 0" type="warning" dense text>
            Tiene {{ getUsos(toDelete) }} PQR externo(s) asociado(s). Esos registros conservan
            el nombre y no se pierden, pero la opcion deja de estar disponible en la app.
            Si solo queres sacarla del desplegable, conviene marcarla como inactiva.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text :disabled="eliminando" @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" :disabled="eliminando" :loading="eliminando" @click="confirmDelete">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar :color="snackColor" v-model="snackbar">
      {{ text }}
      <v-btn color="white" text @click="snackbar = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { ValidationObserver, ValidationProvider } from 'vee-validate'

// Administra uno de los dos maestros del modulo de PQR externos.
// Se instancia dos veces desde PqrsExternos.vue, una por pestania.
export default {
  name: 'MaestrosPqr',

  components: { ValidationObserver, ValidationProvider },

  props: {
    tipo: {
      type: String,
      required: true,
      validator: v => ['conjunto', 'dependencia'].includes(v)
    }
  },

  data() {
    return {
      search: '',
      dialog: false,
      deleteDialog: false,
      toDelete: null,
      edit: false,
      editId: null,
      guardando: false,
      eliminando: false,
      snackbar: false,
      snackColor: 'success',
      text: '',
      form: {
        name: '',
        active: true
      },
      headers: [
        { text: 'Nombre', align: 'left', value: 'name' },
        { text: 'PQR asociados', value: 'usos', sortable: false },
        { text: 'Estado', value: 'active' },
        { text: 'Opciones', value: 'actions', sortable: false }
      ]
    }
  },

  computed: {
    ...mapState({
      conjuntos: state => state.conjunto.conjuntos,
      dependencias: state => state.dependencia.dependencias,
      pqrsExternos: state => state.pqrsExterno.pqrsExternos
    }),

    esConjunto() {
      return this.tipo === 'conjunto'
    },

    items() {
      return this.esConjunto ? this.conjuntos : this.dependencias
    },

    singular() {
      return this.esConjunto ? 'conjunto' : 'dependencia'
    },

    plural() {
      return this.esConjunto ? 'conjuntos' : 'dependencias'
    },

    placeholder() {
      return this.esConjunto ? 'Ej: Conjunto Residencial Los Cerezos' : 'Ej: Administracion'
    },

    subtitulo() {
      return this.esConjunto
        ? 'Conjuntos que aparecen en el desplegable de la app movil'
        : 'Dependencias a las que se puede dirigir un PQR externo'
    },

    // Campos del PQR que referencian a este maestro.
    campoReferencia() {
      return this.esConjunto ? 'conjuntoId' : 'dependenciaId'
    },

    campoNombre() {
      return this.esConjunto ? 'conjuntoNombre' : 'dependenciaNombre'
    },

    // Cuantos PQR externos referencian cada maestro, para avisar antes de borrar.
    // Solo es un numero real para quien ve todos los PQR: para un gestor la
    // coleccion viene filtrada, asi que un conteo daria un dato enganioso.
    // Se calcula una sola vez por render y no una vez por fila.
    usosPorId() {
      if (!this.$store.getters.puede('pqr.verTodas')) return null

      const conteo = {}
      const campo = this.campoReferencia
      ;(this.pqrsExternos || []).forEach(p => {
        const id = p[campo]
        if (id) conteo[id] = (conteo[id] || 0) + 1
      })
      return conteo
    }
  },

  methods: {
    // `null` significa "no se puede saber", que es distinto de "cero".
    getUsos(item) {
      if (!item || !this.usosPorId) return null
      return this.usosPorId[item.id] || 0
    },

    openModal() {
      this.edit = false
      this.editId = null
      this.form = { name: '', active: true }
      this.dialog = true
      this.resetValidation()
    },

    editItem(item) {
      this.edit = true
      this.editId = item.id
      this.form = {
        name: item.name || '',
        active: item.active !== false
      }
      this.dialog = true
      this.resetValidation()
    },

    resetValidation() {
      this.$nextTick(() => {
        if (this.$refs.obs) this.$refs.obs.reset()
      })
    },

    deleteItem(item) {
      this.toDelete = item
      this.deleteDialog = true
    },

    async confirmDelete() {
      // Sin la guarda, un doble clic dispara dos borrados y el segundo falla
      // con el dialogo ya cerrado y `toDelete` en null.
      if (this.eliminando || !this.toDelete) return
      this.eliminando = true

      const action = this.esConjunto ? 'conjunto/deleteConjunto' : 'dependencia/deleteDependencia'
      try {
        await this.$store.dispatch(action, this.toDelete.id)
        this.notify('Registro eliminado correctamente', 'success')
        this.deleteDialog = false
        this.toDelete = null
      } catch (e) {
        // El dialogo queda abierto para poder reintentar.
        this.notify('Error al eliminar el registro', 'error')
      } finally {
        this.eliminando = false
      }
    },

    // Compara ignorando mayusculas, tildes y espacios de mas, que es como una
    // persona percibe que dos nombres son "el mismo".
    normalizar(valor) {
      const tildes = { 'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u', 'ü': 'u' }
      return (valor || '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .replace(/[áéíóúü]/g, c => tildes[c])
    },

    async formSubmit() {
      // Sin la guarda, un doble clic (o un Enter mientras el primer guardado
      // sigue en vuelo) crea el maestro dos veces.
      if (this.guardando) return

      const name = (this.form.name || '').trim().replace(/\s+/g, ' ')
      if (!name) return

      const duplicado = this.items.some(
        i => i.id !== this.editId && this.normalizar(i.name) === this.normalizar(name)
      )
      if (duplicado) {
        this.notify('Ya existe un registro con ese nombre', 'error')
        return
      }

      const data = { name, active: this.form.active }
      const createAction = this.esConjunto ? 'conjunto/createConjunto' : 'dependencia/createDependencia'
      const updateAction = this.esConjunto ? 'conjunto/updateConjunto' : 'dependencia/updateDependencia'

      this.guardando = true

      try {
        if (this.edit) {
          await this.$store.dispatch(updateAction, { id: this.editId, data })
        } else {
          await this.$store.dispatch(createAction, data)
        }
      } catch (e) {
        this.notify('Error al guardar el registro', 'error')
        this.guardando = false
        return
      }

      // La propagacion del nombre va fuera del try del guardado: si falla, el
      // maestro YA quedo guardado y avisar "error al guardar" seria mentira.
      // Se dispara en TODA edicion y no solo cuando cambia el nombre: la accion
      // ya saltea los PQR que estan al dia, asi que cuesta una consulta y cero
      // escrituras, y a cambio volver a guardar el maestro reintenta de verdad
      // una propagacion que haya quedado a mitad de camino.
      if (this.edit) {
        try {
          const actualizados = await this.$store.dispatch('pqrsExterno/propagarNombreMaestro', {
            campoId: this.campoReferencia,
            campoNombre: this.campoNombre,
            id: this.editId,
            nombre: name
          })
          this.notify(
            actualizados > 0
              ? `Registro actualizado. Se renombro tambien en ${actualizados} PQR.`
              : 'Registro actualizado correctamente',
            'success'
          )
        } catch (e) {
          this.notify(
            'El registro se guardo, pero no se pudo actualizar el nombre en los PQR ya cargados. Volve a editarlo y guardalo para reintentar.',
            'warning'
          )
        }
      } else {
        this.notify(
          this.edit ? 'Registro actualizado correctamente' : 'Registro creado correctamente',
          'success'
        )
      }

      this.dialog = false
      this.guardando = false
    },

    notify(message, color) {
      this.text = message
      this.snackColor = color
      this.snackbar = true
    }
  }
}
</script>
